/**
 * Iter 95 — Contexte régional (pays, devise, formatage) pour tout le frontend.
 * Le backend est la source d'autorité : GET /api/region résout la chaîne
 * compte > cookie signé > en-têtes > locale navigateur > IP.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
  DEFAULT_REGION, cacheRegion, formatMoney, getCachedRegion,
} from '../utils/currency';

const API = process.env.REACT_APP_BACKEND_URL;
const RegionContext = createContext(null);

export const RegionProvider = ({ children }) => {
  const [region, setRegion] = useState(() => getCachedRegion() || DEFAULT_REGION);

  const refresh = useCallback(async () => {
    try {
      const token = window.localStorage.getItem('token');
      const res = await fetch(`${API}/api/region`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      setRegion(data);
      cacheRegion(data);
    } catch {
      /* détection jamais bloquante */
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Choix explicite du visiteur (page géo, bannière) : cookie signé côté serveur puis re-synchronisation
  const chooseCountry = useCallback(async (code) => {
    try {
      await fetch(`${API}/api/region/cookie`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ country: code }),
      });
      await refresh();
    } catch { /* jamais bloquant */ }
  }, [refresh]);

  const value = useMemo(() => ({
    region,
    country: region.country || 'CH',
    currency: region.currency || 'CHF',
    currencySymbol: region.currency_symbol || region.currency || 'CHF',
    checkoutCurrency: region.checkout_currency
      || (['CH', 'LI'].includes(region.country) ? 'CHF' : 'EUR'),
    fmt: (n) => formatMoney(n, region),
    refresh,
    chooseCountry,
  }), [region, refresh, chooseCountry]);

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

export const useRegion = () => {
  const ctx = useContext(RegionContext);
  if (ctx) return ctx;
  const region = getCachedRegion() || DEFAULT_REGION;
  return {
    region,
    country: region.country || 'CH',
    currency: region.currency || 'CHF',
    currencySymbol: region.currency_symbol || region.currency || 'CHF',
    checkoutCurrency: region.checkout_currency || 'CHF',
    fmt: (n) => formatMoney(n, region),
    refresh: () => {},
    chooseCountry: async () => {},
  };
};
